import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Loader2, RefreshCw, Search } from 'lucide-react';
import {
  getAmbulanceOffHoursClicks,
  AmbulanceOffHoursRow,
  AmbulanceOffHoursTotals,
} from '../../services/MasterAdmin/ambulanceOffHoursAnalyticsService';
import MetricInfoPopover, { MetricInfoDefinition } from '../../components/MasterAdmin/ui/MetricInfoPopover';
import { ambulanceOffHoursMetricInfo } from '../../config/masterAdminMetricDefinitions';

const DAYS_OPTIONS = [30, 90, 180, 365];

const AmbulanceOffHoursAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(90);
  const [totals, setTotals] = useState<AmbulanceOffHoursTotals | null>(null);
  const [rows, setRows] = useState<AmbulanceOffHoursRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAmbulanceOffHoursClicks(days);
      if (res.success && res.data) {
        setTotals(res.data.totals);
        setRows(res.data.rows);
      } else {
        setError(res.message || 'Failed to load analytics');
      }
    } catch (err: unknown) {
      console.error(err);
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(msg || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredRows = useMemo(() => {
    if (!searchText.trim()) return rows;
    const q = searchText.toLowerCase();
    return rows.filter(
      (r) =>
        (r.userId || '').toLowerCase().includes(q) ||
        (r.displayName || '').toLowerCase().includes(q) ||
        (r.phoneNumber || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q)
    );
  }, [rows, searchText]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 dark:bg-gray-900 md:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-end gap-3">
          <label className="flex h-10 items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="shrink-0">Range</span>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-10 min-w-[10rem] rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              {DAYS_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  Last {d} days
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>

        {error ? (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
            {error}
          </div>
        ) : null}

        {totals && !loading ? (
          <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <StatCard label="Total clicks" value={totals.total} accent="border-l-4 border-l-gray-700" info={ambulanceOffHoursMetricInfo.totalClicks} />
            <StatCard label="Android" value={totals.android} accent="border-l-4 border-l-green-600" info={ambulanceOffHoursMetricInfo.androidClicks} />
            <StatCard label="iOS" value={totals.ios} accent="border-l-4 border-l-slate-600" info={ambulanceOffHoursMetricInfo.iosClicks} />
            <StatCard label="Web" value={totals.web} accent="border-l-4 border-l-blue-600" info={ambulanceOffHoursMetricInfo.webClicks} />
            <StatCard
              label="Unique users"
              value={rows.length}
              accent="border-l-4 border-l-amber-500"
              info={ambulanceOffHoursMetricInfo.uniqueUsers}
            />
          </div>
        ) : loading ? (
          <div className="mb-8 flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : null}

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 p-4 dark:border-gray-700">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                placeholder="Search by user id, name, phone…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                    Total
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                    Android
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                    iOS
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                    Web
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                    Last click
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                      {loading ? 'Loading…' : 'No events in this range.'}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r) => (
                    <tr key={r.userId} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {r.displayName || '—'}
                        </div>
                        <div className="mt-0.5 max-w-xs truncate font-mono text-xs text-gray-500" title={r.userId}>
                          {r.userId}
                        </div>
                        {r.userKind === 'anonymous_web' ? (
                          <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                            Web anonymous
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {r.phoneNumber || '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                        {r.totalClicks}
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">
                        {r.androidClicks}
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">
                        {r.iosClicks}
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">
                        {r.webClicks}
                      </td>
                      <td className="px-4 py-3 text-left text-xs text-gray-600 dark:text-gray-400">
                        {r.lastClickAt ? new Date(r.lastClickAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-center text-sm">
                        {r.userKind !== 'anonymous_web' ? (
                          <button
                            onClick={() => navigate(`/users/${r.userId}`)}
                            className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                            title="View user analytics"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

function StatCard({
  label,
  value,
  accent,
  info,
}: {
  label: string;
  value: number;
  accent: string;
  info?: MetricInfoDefinition;
}) {
  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 ${accent}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</span>
        {info ? <MetricInfoPopover title={label} info={info} /> : null}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}

export default AmbulanceOffHoursAnalyticsPage;
